import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { StyledTextField } from '@modules/ui-components';
import type { AdCreativeAssetReference, AdCreativeItem } from '@segmently/schema-models/ad-creative';
import type {
  AdCreativeVideoVoiceRightsLibraryView,
  AdCreativeVideoVoiceRightsSummaryView,
  AdCreativeVoiceRightsBasis,
  AdCreativeVoiceRightsOperation,
} from '../../services/AdCreativeLocalizationApiContract';
import type { AdCreativeApiClient } from '../../services/AdCreativeApiClient';
import { exactSourceVideoAsset } from './videoLocalizationSource';

type VideoVoiceRightsClient = Pick<
  AdCreativeApiClient,
  | 'listItemVideoVoiceRights'
  | 'approveItemVideoVoiceRights'
  | 'revokeItemVideoVoiceRights'
>;

export interface AdCreativeVideoVoiceRightsSelection {
  evidenceId: string;
  voiceId: string;
  allowedOperations: AdCreativeVoiceRightsOperation[];
}

export interface VideoVoiceRightsPanelProps {
  projectId: string;
  item: AdCreativeItem;
  slotKey: string;
  targetLocale: string;
  client: VideoVoiceRightsClient;
  selectedEvidenceId?: string;
  disabled?: boolean;
  onSelect: (selection: AdCreativeVideoVoiceRightsSelection | null) => void;
}

const RIGHTS_BASES: Array<{ value: AdCreativeVoiceRightsBasis; label: string }> = [
  { value: 'owner_recorded', label: 'Recorded and owned by us' },
  { value: 'talent_consent', label: 'Talent consent' },
  { value: 'licensed_voice', label: 'Licensed voice' },
  { value: 'synthetic_voice_license', label: 'Synthetic voice license' },
  { value: 'contractual_permission', label: 'Contractual permission' },
];

const OPERATIONS: Array<{ value: AdCreativeVoiceRightsOperation; label: string }> = [
  { value: 'replace', label: 'Replace speech with approved TTS' },
  { value: 'dub', label: 'Dub when supported' },
  { value: 'mix', label: 'Mix approved speech with source audio' },
];

export function VideoVoiceRightsPanel({
  projectId,
  item,
  slotKey,
  targetLocale,
  client,
  selectedEvidenceId,
  disabled = false,
  onSelect,
}: VideoVoiceRightsPanelProps): React.ReactElement {
  const [library, setLibrary] = useState<AdCreativeVideoVoiceRightsLibraryView | null>(null);
  const [voiceId, setVoiceId] = useState('');
  const [rightsBasis, setRightsBasis] = useState<AdCreativeVoiceRightsBasis>('talent_consent');
  const [allowedOperations, setAllowedOperations] = useState<AdCreativeVoiceRightsOperation[]>(['replace']);
  const [expiresAt, setExpiresAt] = useState('');
  const [sourceDocumentRef, setSourceDocumentRef] = useState('');
  const [sourceDocumentSha256, setSourceDocumentSha256] = useState('');
  const [statement, setStatement] = useState('');
  const [revocationReason, setRevocationReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const identityRef = useRef('');

  const sourceAsset = useMemo(() => exactSourceVideoAsset(item, slotKey), [item, slotKey]);
  const identity = `${projectId}:${item.id}:${item.sourceRevision}:${slotKey}:${targetLocale}`;
  identityRef.current = identity;
  const exactRequest = useMemo(() => ({
    projectId,
    itemId: item.id,
    slotKey,
    targetLocale,
  }), [item.id, projectId, slotKey, targetLocale]);

  const acceptLibrary = useCallback((next: AdCreativeVideoVoiceRightsLibraryView): void => {
    if (!sourceAsset) throw new Error('This slot has no exact source-locale Segmently video.');
    assertExactVoiceRightsLibrary(next, item, slotKey, targetLocale, sourceAsset);
    setLibrary(next);
    if (selectedEvidenceId) {
      const selected = next.evidence.find((entry) => entry.evidenceId === selectedEvidenceId);
      if (!selected || selected.status !== 'active') onSelect(null);
    }
  }, [item, onSelect, selectedEvidenceId, slotKey, sourceAsset, targetLocale]);

  const loadLibrary = useCallback(async (expectedIdentity = identity): Promise<void> => {
    if (!sourceAsset) throw new Error('This slot has no exact source-locale Segmently video.');
    const response = await client.listItemVideoVoiceRights(exactRequest);
    if (!response.success || !response.data?.library) {
      throw new Error(response.error || 'Voice-rights evidence could not be loaded.');
    }
    if (identityRef.current === expectedIdentity) acceptLibrary(response.data.library);
  }, [acceptLibrary, client, exactRequest, identity, sourceAsset]);

  useEffect(() => {
    let active = true;
    setLibrary(null);
    setVoiceId('');
    setRightsBasis('talent_consent');
    setAllowedOperations(['replace']);
    setExpiresAt('');
    setSourceDocumentRef('');
    setSourceDocumentSha256('');
    setStatement('');
    setRevocationReason('');
    setError(null);
    setStatus(null);
    setLoading(true);
    void loadLibrary(identity)
      .catch((nextError) => {
        if (active && identityRef.current === identity) setError(errorMessage(nextError));
      })
      .finally(() => {
        if (active && identityRef.current === identity) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [identity, loadLibrary]);

  const approve = async (): Promise<void> => {
    const expectedIdentity = identity;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const response = await client.approveItemVideoVoiceRights({
        ...exactRequest,
        voiceId,
        rightsBasis,
        allowedOperations,
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
        sourceDocumentRef,
        sourceDocumentSha256,
        statement,
      });
      if (!response.success || response.data?.action !== 'approved' || !response.data.library) {
        throw new Error(response.error || 'Voice-rights evidence could not be approved.');
      }
      if (identityRef.current !== expectedIdentity) return;
      acceptLibrary(response.data.library);
      const summary = response.data.summary;
      if (summary.status !== 'active') throw new Error('The new evidence is not active.');
      onSelect(toSelection(summary));
      setVoiceId('');
      setSourceDocumentRef('');
      setSourceDocumentSha256('');
      setStatement('');
      setExpiresAt('');
      setStatus(response.data.reused
        ? 'Existing immutable approval reused and selected.'
        : 'Immutable voice-rights approval created and selected.');
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setBusy(false);
    }
  };

  const revoke = async (): Promise<void> => {
    if (!selectedEvidenceId) return;
    const expectedIdentity = identity;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const response = await client.revokeItemVideoVoiceRights({
        ...exactRequest,
        evidenceId: selectedEvidenceId,
        reason: revocationReason,
      });
      if (!response.success || response.data?.action !== 'revoked' || !response.data.library) {
        throw new Error(response.error || 'Voice-rights evidence could not be revoked.');
      }
      if (identityRef.current !== expectedIdentity) return;
      acceptLibrary(response.data.library);
      onSelect(null);
      setRevocationReason('');
      setStatus(response.data.reused
        ? 'This immutable approval was already revoked.'
        : 'Voice-rights approval revoked. No existing evidence was deleted.');
    } catch (nextError) {
      if (identityRef.current === expectedIdentity) setError(errorMessage(nextError));
    } finally {
      if (identityRef.current === expectedIdentity) setBusy(false);
    }
  };

  const activeEvidence = library?.evidence.filter((entry) => entry.status === 'active') ?? [];
  const selectedSummary = library?.evidence.find((entry) => entry.evidenceId === selectedEvidenceId);
  const approvalReady = voiceId.trim().length > 0
    && allowedOperations.length > 0
    && validOpaqueDocumentRef(sourceDocumentRef)
    && /^[a-f0-9]{64}$/iu.test(sourceDocumentSha256.trim())
    && new TextEncoder().encode(statement.trim()).byteLength >= 20
    && (!expiresAt || Date.parse(expiresAt) > Date.now());

  return (
    <Box component="section" aria-labelledby="video-voice-rights-heading" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography id="video-voice-rights-heading" variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
            Approved voice rights
          </Typography>
          {selectedSummary?.status === 'active' && <Chip size="small" color="success" label="Selected" />}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Rights are attached to the exact current Segmently-copy video, voice, target language, and allowed operation. They never change the source or anything in Meta.
        </Typography>

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" role="status">
            <CircularProgress size={14} />
            <Typography variant="caption">Loading exact-lineage approvals…</Typography>
          </Stack>
        )}

        {library && (
          <Stack spacing={1}>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 0.75 }}>
              <Typography variant="caption" component="div">
                {library.sourceLocale} → {library.targetLocale} · revision {library.sourceRevision}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ overflowWrap: 'anywhere' }}>
                Asset {library.sourceAssetId}
              </Typography>
            </Box>
            {library.truncated && (
              <Alert severity="warning">Only the newest 100 approvals are shown. Revoke or archive obsolete approvals before adding more.</Alert>
            )}
            {activeEvidence.length === 0 ? (
              <Alert severity="info">No active approval matches this exact video and target language.</Alert>
            ) : (
              <StyledTextField
                select
                fullWidth
                size="small"
                label="Active voice approval"
                value={selectedEvidenceId ?? ''}
                disabled={disabled || busy}
                onChange={(value) => {
                  const selected = activeEvidence.find((entry) => entry.evidenceId === String(value));
                  onSelect(selected ? toSelection(selected) : null);
                }}
              >
                <MenuItem value="">Select an approval</MenuItem>
                {activeEvidence.map((entry) => (
                  <MenuItem key={entry.evidenceId} value={entry.evidenceId}>
                    {entry.voiceId} · {entry.allowedOperations.join(', ')} · {formatDate(entry.createdAt)}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}

            <Stack spacing={0.75} aria-label="Voice-rights evidence history">
              {library.evidence.map((entry) => (
                <Box key={entry.evidenceId} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 0.75 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{entry.voiceId}</Typography>
                    <Chip size="small" color={entry.status === 'active' ? 'success' : 'default'} label={entry.status} />
                    <Chip size="small" variant="outlined" label={entry.allowedOperations.join(' · ')} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ overflowWrap: 'anywhere' }}>
                    {entry.rightsBasis.replace(/_/gu, ' ')} · {entry.evidenceId}
                  </Typography>
                  {entry.expiresAt && <Typography variant="caption" color="text.secondary">Expires {formatDate(entry.expiresAt)}</Typography>}
                </Box>
              ))}
            </Stack>

            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
              <Stack spacing={0.75}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Record a new immutable approval</Typography>
                <StyledTextField
                  fullWidth
                  size="small"
                  label="Approved voice ID"
                  value={voiceId}
                  disabled={disabled || busy}
                  onChange={(value) => setVoiceId(String(value))}
                />
                <StyledTextField
                  select
                  fullWidth
                  size="small"
                  label="Rights basis"
                  value={rightsBasis}
                  disabled={disabled || busy}
                  onChange={(value) => setRightsBasis(String(value) as AdCreativeVoiceRightsBasis)}
                >
                  {RIGHTS_BASES.map((entry) => <MenuItem key={entry.value} value={entry.value}>{entry.label}</MenuItem>)}
                </StyledTextField>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Allowed operations</Typography>
                  {OPERATIONS.map((entry) => (
                    <FormControlLabel
                      key={entry.value}
                      control={(
                        <Checkbox
                          size="small"
                          checked={allowedOperations.includes(entry.value)}
                          disabled={disabled || busy}
                          onChange={(event) => setAllowedOperations((current) => event.target.checked
                            ? Array.from(new Set([...current, entry.value]))
                            : current.filter((value) => value !== entry.value))}
                        />
                      )}
                      label={entry.label}
                    />
                  ))}
                </Box>
                <StyledTextField
                  fullWidth
                  size="small"
                  label="Consent or license record ID"
                  helperText="Opaque internal reference only. URLs, tokens, signatures, and credentials are rejected."
                  value={sourceDocumentRef}
                  disabled={disabled || busy}
                  onChange={(value) => setSourceDocumentRef(String(value))}
                />
                <StyledTextField
                  fullWidth
                  size="small"
                  label="Consent or license document SHA-256"
                  value={sourceDocumentSha256}
                  disabled={disabled || busy}
                  onChange={(value) => setSourceDocumentSha256(String(value).trim().toLowerCase())}
                />
                <StyledTextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="Rights approval statement"
                  helperText="State who granted which voice rights and for this target language. Minimum 20 bytes."
                  value={statement}
                  disabled={disabled || busy}
                  onChange={(value) => setStatement(String(value))}
                />
                <StyledTextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Expires on (optional)"
                  value={expiresAt}
                  disabled={disabled || busy}
                  InputLabelProps={{ shrink: true }}
                  onChange={(value) => setExpiresAt(String(value))}
                />
                <Button size="small" variant="contained" disabled={disabled || busy || !approvalReady} onClick={() => void approve()}>
                  {busy ? 'Working…' : 'Approve and select voice'}
                </Button>
              </Stack>
            </Box>

            {selectedSummary?.status === 'active' && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
                <Stack spacing={0.75}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Revoke selected approval</Typography>
                  <StyledTextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="Revocation reason"
                    value={revocationReason}
                    disabled={disabled || busy}
                    onChange={(value) => setRevocationReason(String(value))}
                  />
                  <Button color="error" size="small" variant="outlined" disabled={disabled || busy || revocationReason.trim().length < 3} onClick={() => void revoke()}>
                    Revoke approval
                  </Button>
                </Stack>
              </Box>
            )}
          </Stack>
        )}

        {error && <Alert severity="error" role="alert">{error}</Alert>}
        {status && <Alert severity="success" role="status">{status}</Alert>}
      </Stack>
    </Box>
  );
}

function toSelection(summary: AdCreativeVideoVoiceRightsSummaryView): AdCreativeVideoVoiceRightsSelection {
  return {
    evidenceId: summary.evidenceId,
    voiceId: summary.voiceId,
    allowedOperations: summary.allowedOperations,
  };
}

function assertExactVoiceRightsLibrary(
  library: AdCreativeVideoVoiceRightsLibraryView,
  item: AdCreativeItem,
  slotKey: string,
  targetLocale: string,
  sourceAsset: AdCreativeAssetReference,
): void {
  if (item.entityKind !== 'segmently_copy'
    || library.creativeItemId !== item.id
    || library.slotKey !== slotKey
    || library.sourceRevision !== item.sourceRevision
    || library.sourceLocale !== item.defaultLanguage
    || library.targetLocale !== targetLocale
    || library.sourceAssetId !== sourceAsset.assetId
    || library.sourceAssetSha256 !== sourceAsset.sha256) {
    throw new Error('Voice-rights library does not match the exact current Segmently-copy video lineage.');
  }
}

function validOpaqueDocumentRef(value: string): boolean {
  const normalized = value.trim();
  return /^[A-Za-z0-9][A-Za-z0-9._:/@-]*$/u.test(normalized)
    && !/:\/\//u.test(normalized)
    && !/[?#]/u.test(normalized)
    && !/(?:token|signature|credential|authorization|bearer)[:=]/iu.test(normalized);
}

function formatDate(value: string): string {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toLocaleString() : value;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
